import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { AutocompleteChangeReason, useTheme } from '@mui/material'
import Alert from '@mui/material/Alert'
import Autocomplete, { AutocompleteInputChangeReason } from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { ChangeEvent, Fragment, MouseEvent, ReactNode, SyntheticEvent, useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useBookingContext } from '@providers/booking'
import { TBookingPet } from '@providers/booking/booking'
import { pl, i18WithDefault as t } from '@shared/locale'
import { requiresTemperTestForSpecie, requiresWeightForSpecie, temperTestAheadTime } from '@shared/service_types'
import { Spacer1, Spacer2, TextInputNumber } from 'components-ui'
import { Types } from 'service-api'
import { AddButton } from './add_button'
import { AutocompletePetName } from './autocomplete_pet_name'
import { ErrorMessages } from './error_messages'
import { PetThresholds } from './pet_thresholds'
import { BookingSpeciesButtons } from './species_button'

export const PetSearchSimple = ({ children }: { children: ReactNode }) => {
  const bookingCtx = useBookingContext()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const { serviceTypeName = '' } = useParams()
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))

  const onClick = useCallback(
    (ev: MouseEvent<HTMLElement>) => {
      if (bookingCtx.state.config.location?.pricingStrategy) {
        bookingCtx.dispatch({ type: 'TOGGLE_SEARCH_BAR', payload: { action: 'open' } })
        setAnchorEl(ev.currentTarget)
      }
    },
    [bookingCtx]
  )
  const onCloseModal = useCallback(() => {
    bookingCtx.dispatch({ type: 'TOGGLE_SEARCH_BAR', payload: { action: 'close' } })
    setAnchorEl(null)
  }, [bookingCtx])
  const onChangePetData = useCallback(
    (ev: ChangeEvent<HTMLInputElement>) => {
      const { target: t } = ev
      bookingCtx.dispatch({
        type: 'SET_PET_SEARCH_FIELD',
        payload: {
          fieldName: t.name,
          value: t.value,
          groupIdx: t.dataset.group,
          petIdx: t.dataset.pos,
        },
      })
    },
    [bookingCtx]
  )
  const onBlurField = useCallback(
    (ev: React.FocusEvent<HTMLInputElement>) => {
      const { currentTarget: t } = ev
      const nodeAttrs = ev.currentTarget.attributes
      bookingCtx.dispatch({
        type: 'BLUR_FIELD_VALIDATION',
        payload: {
          fieldName: nodeAttrs.getNamedItem('name')?.value,
          groupIdx: t.dataset.group,
          petIdx: t.dataset.pos,
        },
      })
    },
    [bookingCtx]
  )

  const onSelectBreed = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (ev: SyntheticEvent, value: Types.BreedClientDto | null, reason: AutocompleteChangeReason) => {
      const { currentTarget: t } = ev as SyntheticEvent<HTMLInputElement>
      if (reason === 'clear') {
        const input = t.closest('.MuiAutocomplete-root') as HTMLElement
        if (input) {
          bookingCtx.dispatch({
            type: 'CLEAR_PET_SEARCH_BREED_FIELD',
            payload: {
              groupIdx: input.dataset.group,
              petIdx: input.dataset.pos,
            },
          })
        }
        return
      }

      if (!value || typeof value === 'string') {
        return
      }

      bookingCtx.dispatch({
        type: 'SET_PET_SEARCH_BREED_FIELD',
        payload: {
          breed: value.displayName,
          breedId: value.id,
          groupIdx: t.dataset.group,
          petIdx: t.dataset.pos,
        },
      })
    },
    [bookingCtx]
  )
  const onChangeBreedInput = useCallback(
    (ev: SyntheticEvent, newValue: string, reason: AutocompleteInputChangeReason) => {
      if (!ev || (reason === 'reset' && !newValue)) {
        return
      }
      let { currentTarget: t } = ev as SyntheticEvent<HTMLInputElement>
      if (!t.dataset.group) {
        t = t.closest('.MuiAutocomplete-root')!
      }

      bookingCtx.dispatch({
        type: 'SET_BREED_INPUT_FIELD',
        payload: {
          groupIdx: t.dataset.group,
          petIdx: t.dataset.pos,
          value: newValue,
        },
      })
    },
    [bookingCtx]
  )

  const onRemovePetRecord = useCallback(
    (ev: MouseEvent<HTMLButtonElement>) => {
      const { dataset } = ev.currentTarget
      bookingCtx.dispatch({
        type: 'REMOVE_PET_SEARCH_FIELD',
        payload: { groupIdx: dataset.group, petIdx: dataset.pet },
      })
    },
    [bookingCtx]
  )
  const onRemoveRoom = useCallback(
    (ev: MouseEvent<HTMLButtonElement>) => {
      const { dataset } = ev.currentTarget
      bookingCtx.dispatch({
        type: 'REMOVE_ROOM_SEARCH_FIELD',
        payload: { roomIdx: dataset.group },
      })
    },
    [bookingCtx]
  )

  const onCheckRoomWeight = useCallback(
    (ev: React.FocusEvent<HTMLInputElement>) => {
      const { dataset } = ev.currentTarget
      bookingCtx.dispatch({ type: 'CHECK_ROOM_WEIGHT', payload: { groupIdx: dataset.group } })
      onBlurField(ev)
    },
    [bookingCtx, onBlurField]
  )

  const onCheckTemperTest = useCallback(
    (ev: React.SyntheticEvent<HTMLInputElement>, checked: boolean) => {
      const { dataset } = ev.currentTarget
      bookingCtx.dispatch({
        type: 'SET_TEMPER_TEST_REQUIREMENT',
        payload: { groupIdx: dataset.group, petIdx: dataset.pet, value: !checked },
      })
    },
    [bookingCtx]
  )

  const onDoneClick = useCallback(
    (_ev: MouseEvent<HTMLButtonElement>) => {
      bookingCtx.dispatch({ type: 'SET_DONE_FORM_VALIDATION' })
    },
    [bookingCtx]
  )

  const { config } = bookingCtx.state
  const { groups } = bookingCtx.state
  const hasTemperTest = useMemo(() => {
    const speciesIds = bookingCtx.state.config.species?.map(({ id }) => id!) || []
    return speciesIds.reduce((acc, si) => {
      acc[si] = requiresTemperTestForSpecie(si, bookingCtx.state.config.booking)
      return acc
    }, {} as GenericSimpleBag)
  }, [bookingCtx.state.config])
  const temperTestLegend = useMemo(() => {
    const legend = t('searchBar.temperTest')
    const defaultName = t('temperTest.defaultName')
    const leadTime = temperTestAheadTime(bookingCtx.state)
    return legend
      .replace('{{temperTestName}}', bookingCtx.state.data.temperTest?.displayName || defaultName)
      .replace('{{leadTime}}', pl(t('global.label.day'), leadTime, true))
      .replace('{{serviceTypeName}}', serviceTypeName)
  }, [bookingCtx.state, serviceTypeName])
  const requiresWeight = useMemo(
    () => requiresWeightForSpecie(bookingCtx.state.config.booking),
    [bookingCtx.state.config.booking]
  )

  return (
    <Box>
      <Box onClick={onClick} sx={{ cursor: 'pointer' }}>
        {children}
      </Box>
      {anchorEl && (
        <Popover
          open={bookingCtx.state.searchBarOpen}
          onClose={onCloseModal}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          anchorEl={anchorEl}
        >
          <Paper elevation={0} sx={{ p: 2, maxWidth: '650px', width: `${isBelowMD ? 'calc(100vw - 32px)' : '650px'}` }}>
            {groups.map((group, groupIdx) => {
              const groupWithError = bookingCtx.state.groups[groupIdx].errors.length > 0
              return (
                <Box key={group.id}>
                  <>
                    <Stack direction="column" alignItems="start">
                      <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                        <Typography variant="h6" component="span">
                          <strong style={{ textTransform: 'capitalize' }}>
                            {`${t('global.label.room')} ${groupIdx + 1}`}
                          </strong>
                        </Typography>
                        <Stack direction="row">
                          <BookingSpeciesButtons group={groupIdx} />
                          {groups.length > 1 && (
                            <Box sx={{ minWidth: '38px', marginLeft: 1 }}>
                              <Tooltip title={t('global.label.remove')} disableInteractive>
                                <IconButton onClick={onRemoveRoom} sx={{ p: '6px' }} data-group={groupIdx}>
                                  <DeleteOutlineIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Stack>
                      </Stack>
                      <PetThresholds specieId={group.specieId} />
                    </Stack>
                    <Spacer2 />
                  </>

                  {groupWithError && (
                    <>
                      <ErrorMessages groupIdx={groupIdx} />
                      <Spacer2 />
                    </>
                  )}

                  {group.pets.map((_pet, petIdx) => {
                    return (
                      <Fragment key={`${group.id}_${petIdx}`}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1 }} alignItems="start">
                          <AutocompletePetName groupIdx={groupIdx} petIdx={petIdx} />
                          {/* <TextField
                            name="name"
                            label={t('global.label.name')}
                            size="small"
                            fullWidth
                            value={group.pets[petIdx].name}
                            error={Boolean(group.pets[petIdx].nameErrorMsg)}
                            helperText={group.pets[petIdx].nameErrorMsg}
                            onChange={onChangePetData}
                            onBlur={onBlurField}
                            inputProps={{
                              ['data-group']: groupIdx,
                              ['data-pos']: petIdx,
                            }}
                          /> */}
                          <Autocomplete
                            name="breed"
                            sx={{ minWidth: '200px' }}
                            fullWidth
                            freeSolo
                            size="small"
                            options={(config.breedsBySpecie?.[group.specieId] || []) as Types.BreedClientDto[]}
                            value={group.pets[petIdx] as TBookingPet}
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            onChange={onSelectBreed}
                            onBlur={onBlurField}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t('global.label.breed')}
                                error={Boolean(group.pets[petIdx].breedErrorMsg)}
                                helperText={group.pets[petIdx].breedErrorMsg}
                                inputProps={{
                                  ...params.inputProps,
                                }}
                              />
                            )}
                            inputValue={group.pets[petIdx].breedInput}
                            onInputChange={onChangeBreedInput}
                            renderOption={(props, option) => (
                              <li {...props} data-group={groupIdx} data-pos={petIdx}>
                                {option.displayName}
                              </li>
                            )}
                            getOptionLabel={(option) => {
                              if (typeof option === 'string') {
                                return option
                              }
                              return option.displayName || ''
                            }}
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            isOptionEqualToValue={(option: Types.BreedClientDto, value: TBookingPet) =>
                              option.id === value.breedId
                            }
                            data-group={groupIdx}
                            data-pos={petIdx}
                          />
                          {requiresWeight[group.specieId] && (
                            <TextInputNumber
                              name="weight"
                              label={t('global.label.weight')}
                              size="small"
                              value={group.pets[petIdx].weight}
                              onChange={onChangePetData}
                              onBlur={onCheckRoomWeight}
                              error={Boolean(group.pets[petIdx].weightErrorMsg)}
                              helperText={group.pets[petIdx].weightErrorMsg}
                              sx={{ maxWidth: '100px', flexShrink: 0 }}
                              inputProps={{
                                ['data-group']: groupIdx,
                                ['data-pos']: petIdx,
                              }}
                            />
                          )}
                          {group.pets.length > 1 && (
                            <Box sx={{ minWidth: '38px' }}>
                              <Tooltip title={t('global.label.remove')} disableInteractive>
                                <IconButton
                                  onClick={onRemovePetRecord}
                                  sx={{ p: '6px' }}
                                  data-group={groupIdx}
                                  data-pet={petIdx}
                                >
                                  <DeleteOutlineIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Stack>

                        {hasTemperTest[group.specieId] && (
                          <FormGroup>
                            <FormControlLabel
                              label={t('searchBar.newVisitor')}
                              control={
                                <Checkbox
                                  checked={!group.pets[petIdx].temperTested}
                                  onChange={onCheckTemperTest}
                                  inputProps={{
                                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                    // @ts-ignore
                                    ['data-group']: groupIdx,
                                    ['data-pet']: petIdx,
                                  }}
                                />
                              }
                            />
                          </FormGroup>
                        )}

                        <Spacer2 />
                      </Fragment>
                    )
                  })}
                  {hasTemperTest[group.specieId] && (
                    <>
                      <Alert severity="info">
                        <span dangerouslySetInnerHTML={{ __html: temperTestLegend }} />
                      </Alert>
                      <Spacer2 />
                    </>
                  )}

                  <Divider orientation="horizontal" />
                  <Spacer2 />
                </Box>
              )
            })}

            <Spacer1 />
            <AddButton />
            <Spacer1 />

            <Stack
              direction="row"
              justifyContent="center"
              sx={{
                mx: -2,
                mb: -2,
                py: 1,
                position: 'sticky',
                bottom: 0,
                background: theme.palette.background.paper,
                boxShadow: '0px -3px 6px rgba(0, 0, 0, 0.06)',
                zIndex: theme.zIndex.appBar,
              }}
            >
              <Button variant="contained" onClick={onDoneClick} size="large">
                {t('global.label.done')}
              </Button>
            </Stack>
          </Paper>
        </Popover>
      )}
    </Box>
  )
}
